import moment from "moment";
import { CoverageOption, Driver, MonthlyReportData, MonthlyReportDataUnit, Policy, Tractor, Trailer } from "../../northstar";
import { MonthlyReportHelper } from "./MonthlyReportHelper";

/** The coverages an independent contractor is billed for. */
export const IC_COVERAGE_IDS: CoverageOption.Id[] = [
    CoverageOption.Id.NON_TRUCKING_LIABILITY,
    CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE,
    CoverageOption.Id.TRUXPRO,
    CoverageOption.Id.OCCUPATIONAL_ACCIDENT,
];

/** Source-specific PD keys used by the Reporting tab; legacy exports can still combine PD. */
export type ICBilledCoverageId = CoverageOption.Id | "primary-pd" | "ntlpd-pd";

export const IC_BILLED_COVERAGE_IDS: ICBilledCoverageId[] = IC_COVERAGE_IDS.flatMap<ICBilledCoverageId>((id) =>
    id === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE ? ["primary-pd", "ntlpd-pd"] : [id]
);

export function getICBilledCoverageMetadata(id: ICBilledCoverageId) {
    if (id === "primary-pd") return { name: "Physical Damage", defaultAbbreviation: "APD" };
    if (id === "ntlpd-pd") return { name: "Non-Trucking Physical Damage", defaultAbbreviation: "NTLPD" };
    return CoverageOption.getMetadata(id);
}

/** Classify the charge by its policy, never by the driver's policy membership. */
export function getICBilledCoverageId(id: CoverageOption.Id, data: MonthlyReportData): ICBilledCoverageId {
    if (id !== CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) return id;
    const isNTL = [data.startLayer, data.endLayer].some(
        (layer) =>
            layer?.policy?.coveragePackage === Policy.CoveragePackage.NON_TRUCKING ||
            layer?.policy?.coverages?.some((coverage) => coverage.coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY)
    );
    return isNTL ? "ntlpd-pd" : "primary-pd";
}

export interface ICReportRow {
    driverId: number;
    employer: string;
    driverName: string;
    state: string;
    tractorVins: string;
    tractorValues: string;
    trailerVins: string;
    trailerValues: string;
    /** coverageOptionId -> amount billed to this driver */
    costs: Map<ICBilledCoverageId, number>;
    total: number;
    driverDates: string;
    tractorDates: string;
    trailerDates: string;
    equipmentValue: number;
}

export interface ICReportPolicyInput {
    policyNumber: string;
    /** the derived report for this policy and month */
    data: MonthlyReportData;
    /**
     * The account's whole equipment list from the raw query result. Used only to name a unit that is
     * assigned to a driver but sits on no reporting policy this month — without it such a unit would
     * silently disappear from the driver's row.
     */
    equipment?: { tractors: Pick<Tractor, "id" | "vin">[]; trailers: Pick<Trailer, "id" | "vin">[] };
}

interface ResolvedUnit {
    id: number;
    vin?: string;
    isACV?: boolean;
    statedValue?: number | null;
    employerName?: string;
    dateAddedToPolicy?: Date | null;
    dateRemovedFromPolicy?: Date | null;
    isOnReportingPolicy: boolean;
}

const formatDate = (date: Date) => moment.utc(date).format("M/D/YY");

function describeDates(entries: { label: string; added?: Date | null; removed?: Date | null }[]): string {
    return entries
        .flatMap(({ label, added, removed }) => [
            added ? `Added ${label ? `${label} ` : ""}${formatDate(added)}` : null,
            removed ? `Removed ${label ? `${label} ` : ""}${formatDate(removed)}` : null,
        ])
        .filter(Boolean)
        .join(", ");
}

/**
 * Builds one row per independent-contractor driver, rolled up across every policy supplied.
 *
 * A driver's equipment is regularly insured on a different policy than the driver themselves — an
 * owner-operator on an occupational accident policy whose tractor carries physical damage on the
 * auto policy — so costs are summed across all of them rather than taken from any single policy.
 *
 * Only per-unit and stated-value ratings reach the driver. Flat installments, minimum premium
 * top-ups, manual adjustments and mileage/revenue/payroll components belong to the policy rather
 * than to any one truck, so they are deliberately excluded rather than spread across units.
 */
export function buildICReportRows(
    policies: ICReportPolicyInput[],
    options: {
        employerSource?: "driver" | "equipment";
        /** the coverages billed — costs and totals leave out everything else. Defaults to every IC coverage. */
        coverageOptionIds?: CoverageOption.Id[];
        /** When supplied, split PD by source policy and bill only these selections. */
        billedCoverageIds?: ICBilledCoverageId[];
    } = {}
): ICReportRow[] {
    const employerSource = options.employerSource || "driver";
    const coverageOptionIds = options.coverageOptionIds ?? IC_COVERAGE_IDS;

    const billedId = (id: CoverageOption.Id, data: MonthlyReportData) => (options.billedCoverageIds ? getICBilledCoverageId(id, data) : id);
    const isSelected = (id: ICBilledCoverageId) => !options.billedCoverageIds || options.billedCoverageIds.includes(id);

    // the account's whole driver list, so equipment is still priced on policies the driver isn't on
    const allDrivers = new Map<number, MonthlyReportDataUnit<Driver>>();
    for (const { data } of policies) {
        for (const driver of data.equipment.drivers) {
            if (!allDrivers.has(driver.id)) allDrivers.set(driver.id, driver);
        }
    }
    const driverList = [...allDrivers.values()];

    /** Everything accumulated for one driver, held in a single object so lookups can't be undefined. */
    interface DriverAccumulator {
        driver: MonthlyReportDataUnit<Driver>;
        costs: Map<ICBilledCoverageId, number>;
        tractors: Map<string, ResolvedUnit>;
        trailers: Map<string, ResolvedUnit>;
        added: Date | null;
        removed: Date | null;
        // tracked only to decide sort order below — a row is "changed" the moment any of these are
        // true, regardless of which of added/removed/changed it ends up reading as
        addedSomewhere: boolean;
        removedSomewhere: boolean;
        hasCoverageChanges: boolean;
    }

    const accumulators = new Map<number, DriverAccumulator>();
    for (const { data } of policies) {
        for (const driver of data.equipment.drivers) {
            if (driver.isOwnerOperator && !accumulators.has(driver.id)) {
                accumulators.set(driver.id, {
                    driver,
                    costs: new Map(),
                    tractors: new Map(),
                    trailers: new Map(),
                    added: null,
                    removed: null,
                    addedSomewhere: false,
                    removedSomewhere: false,
                    hasCoverageChanges: false,
                });
            }
        }
    }

    /**
     * Keyed on VIN, not id: a truck is sometimes represented by more than one record — typically an
     * old soft-deleted row still linked to the driver alongside the live one. Whichever record is on
     * a policy wins, regardless of the order they arrive.
     */
    const recordUnit = (
        into: Map<string, ResolvedUnit>,
        id: number,
        priced: MonthlyReportDataUnit<Tractor | Trailer> | undefined,
        raw: { id: number; vin?: string | null } | undefined
    ) => {
        const key = priced?.vin || raw?.vin || `id:${id}`;
        if (priced) {
            into.set(key, {
                id,
                vin: priced.vin ?? undefined,
                isACV: priced.isACV,
                statedValue: priced.statedValue,
                employerName: priced.employer?.name,
                dateAddedToPolicy: priced.dateAddedToPolicy,
                dateRemovedFromPolicy: priced.dateRemovedFromPolicy,
                isOnReportingPolicy: true,
            });
        } else if (raw && !into.get(key)?.isOnReportingPolicy) {
            into.set(key, { id, vin: raw.vin ?? undefined, isOnReportingPolicy: false });
        }
    };

    for (const { data, equipment } of policies) {
        const breakdown = MonthlyReportHelper.getDriverPremiumBreakdown(data, driverList);

        const tractorsById = new Map(data.equipment.tractors.map((unit) => [unit.id, unit]));
        const trailersById = new Map(data.equipment.trailers.map((unit) => [unit.id, unit]));
        const rawTractorsById = new Map((equipment?.tractors || []).map((unit) => [unit.id, unit]));
        const rawTrailersById = new Map((equipment?.trailers || []).map((unit) => [unit.id, unit]));
        const driversById = new Map(data.equipment.drivers.map((unit) => [unit.id, unit]));

        for (const [driverId, accumulator] of accumulators.entries()) {
            for (const { id } of accumulator.driver.tractors || []) {
                recordUnit(accumulator.tractors, id, tractorsById.get(id), rawTractorsById.get(id));
            }
            for (const { id } of accumulator.driver.trailers || []) {
                recordUnit(accumulator.trailers, id, trailersById.get(id), rawTrailersById.get(id));
            }

            const onThisPolicy = driversById.get(driverId);
            if (onThisPolicy) {
                accumulator.added = accumulator.added || onThisPolicy.dateAddedToPolicy || null;
                accumulator.removed = accumulator.removed || onThisPolicy.dateRemovedFromPolicy || null;
                accumulator.addedSomewhere = accumulator.addedSomewhere || onThisPolicy.isNewToPolicy;
                accumulator.removedSomewhere = accumulator.removedSomewhere || onThisPolicy.isRemovedFromPolicy;
                accumulator.hasCoverageChanges = accumulator.hasCoverageChanges || onThisPolicy.changes.length > 0;
            }

            const costs = breakdown.get(driverId);
            if (!costs?.size) continue;

            for (const [coverageOptionId, cost] of costs.entries()) {
                if (!coverageOptionIds.includes(coverageOptionId)) continue;
                const id = billedId(coverageOptionId, data);
                if (!isSelected(id)) continue;
                const direct = cost.contributions.reduce((acc, contribution) => acc + contribution.directPremium, 0);
                accumulator.costs.set(id, (accumulator.costs.get(id) ?? 0) + direct);
            }
        }
    }

    const money = (value: number) => value.toLocaleString("en-us", { style: "currency", currency: "USD" });
    const valueOf = (unit: ResolvedUnit) => (unit.isACV ? "ACV" : money(unit.statedValue || 0));
    const labelOf = (unit: ResolvedUnit) => `#${unit.vin?.slice(-4) || unit.id}`;

    const driverRows: { row: ICReportRow; hasChange: boolean }[] = [...accumulators.entries()].map(([driverId, accumulator]) => {
        const { driver, costs } = accumulator;
        const tractors = [...accumulator.tractors.values()];
        const trailers = [...accumulator.trailers.values()];

        const employerNames =
            employerSource === "driver"
                ? [driver.employer?.name].filter(Boolean)
                : [...new Set([...tractors, ...trailers].map((unit) => unit.employerName).filter(Boolean))];

        return {
            row: {
                driverId,
                employer: employerNames.join(", "),
                driverName: driver.name || "",
                state: driver.state || "",
                tractorVins: tractors.map((unit) => unit.vin?.slice(-4) || "N/A").join(", "),
                tractorValues: tractors.map(valueOf).join(", "),
                trailerVins: trailers.map((unit) => unit.vin?.slice(-4) || "N/A").join(", "),
                trailerValues: trailers.map(valueOf).join(", "),
                costs,
                total: [...costs.values()].reduce((acc, amount) => acc + amount, 0),
                driverDates: describeDates([{ label: "", added: accumulator.added, removed: accumulator.removed }]),
                tractorDates: describeDates(
                    tractors.map((unit) => ({
                        label: labelOf(unit),
                        added: unit.dateAddedToPolicy,
                        removed: unit.dateRemovedFromPolicy,
                    }))
                ),
                trailerDates: describeDates(
                    trailers.map((unit) => ({
                        label: labelOf(unit),
                        added: unit.dateAddedToPolicy,
                        removed: unit.dateRemovedFromPolicy,
                    }))
                ),
                equipmentValue: [...tractors, ...trailers].reduce((acc, unit) => acc + (unit.isACV ? 0 : unit.statedValue || 0), 0),
            },
            // matches getChangeTone's account-level "none" check in AccountMonthlyReportUnitsTable:
            // activeSomewhere/continuedSomewhere only decide *which* tone (added/removed/changed) it
            // reads as on screen, never whether it counts as changed at all
            hasChange: accumulator.addedSomewhere || accumulator.removedSomewhere || accumulator.hasCoverageChanges,
        };
    });

    // Tractors carry their own employer, so a tractor nobody currently drives — never assigned to any
    // driver on the account, or orphaned when its driver rolled off — can still be billed and shown
    // once the report is grouped by the equipment's employer instead of the driver's. Mirrors the
    // on-screen account table's driverless-tractor rows.
    const driverlessTractorRows: { row: ICReportRow; hasChange: boolean }[] = [];
    if (employerSource === "equipment") {
        // "assigned" means assigned to one of THIS report's owner-operator rows — `driverList`
        // includes company drivers too, and a tractor sitting only on a company driver is just as
        // invisible as one with no driver at all, since company drivers get no row here
        const assignedTractorIds = new Set<number>();
        for (const { driver } of accumulators.values()) {
            for (const tractor of driver.tractors || []) {
                assignedTractorIds.add(tractor.id);
            }
        }

        const tractorRowById = new Map<number, MonthlyReportDataUnit<Tractor>>();
        for (const { data } of policies) {
            for (const tractor of data.equipment.tractors) {
                if (!tractorRowById.has(tractor.id)) tractorRowById.set(tractor.id, tractor);
            }
        }

        // a driverless tractor has no driver to bill through, so its own direct charges (the same
        // per-unit/stated-value rates that would otherwise flow to a driver) are billed to it
        // directly — computed once per policy and reused across every tractor below
        const unitPremiumsByData = new Map(policies.map(({ data }) => [data, MonthlyReportHelper.getUnitPremiums(data)]));

        for (const [tractorId, tractor] of tractorRowById.entries()) {
            if (assignedTractorIds.has(tractorId)) continue;

            let addedSomewhere = false;
            let removedSomewhere = false;
            let hasCoverageChanges = false;
            let added: Date | null = null;
            let removed: Date | null = null;
            const costs = new Map<ICBilledCoverageId, number>();

            for (const { data } of policies) {
                const onThisPolicy = data.equipment.tractors.find((t) => t.id === tractorId);
                if (!onThisPolicy) continue;

                added = added || onThisPolicy.dateAddedToPolicy || null;
                removed = removed || onThisPolicy.dateRemovedFromPolicy || null;
                addedSomewhere = addedSomewhere || onThisPolicy.isNewToPolicy;
                removedSomewhere = removedSomewhere || onThisPolicy.isRemovedFromPolicy;
                hasCoverageChanges = hasCoverageChanges || onThisPolicy.changes.length > 0;

                const unitPremiums = unitPremiumsByData.get(data);
                for (const coverageOptionId of coverageOptionIds) {
                    const unitPremium = unitPremiums
                        ?.get(coverageOptionId)
                        ?.units.find((u) => u.unitType === "tractor" && u.unitId === tractorId);
                    // matches the driver roll-up above: only per-unit/stated-value charges are billed
                    // here, so only `directPremium` counts, never the residual spread
                    if (!unitPremium?.directPremium) continue;
                    const id = billedId(coverageOptionId, data);
                    if (!isSelected(id)) continue;
                    costs.set(id, (costs.get(id) ?? 0) + unitPremium.directPremium);
                }
            }

            const unit: ResolvedUnit = {
                id: tractor.id,
                vin: tractor.vin ?? undefined,
                isACV: tractor.isACV,
                statedValue: tractor.statedValue,
                employerName: tractor.employer?.name,
                dateAddedToPolicy: tractor.dateAddedToPolicy,
                dateRemovedFromPolicy: tractor.dateRemovedFromPolicy,
                isOnReportingPolicy: true,
            };

            driverlessTractorRows.push({
                row: {
                    // no driver id exists for this row — negated tractor id keeps it unique without
                    // colliding with a real driver id, which is always positive
                    driverId: -tractor.id,
                    employer: tractor.employer?.name || "",
                    driverName: `${labelOf(unit)} · no driver`,
                    state: "",
                    tractorVins: unit.vin?.slice(-4) || "N/A",
                    tractorValues: valueOf(unit),
                    trailerVins: "",
                    trailerValues: "",
                    costs,
                    total: [...costs.values()].reduce((acc, amount) => acc + amount, 0),
                    driverDates: "",
                    tractorDates: describeDates([{ label: "", added, removed }]),
                    trailerDates: "",
                    equipmentValue: unit.isACV ? 0 : unit.statedValue || 0,
                },
                hasChange: addedSomewhere || removedSomewhere || hasCoverageChanges,
            });
        }
    }

    // IC reviewers care about what moved first — surface every changed row (added, removed, or
    // partially changed) above the unchanged ones, keeping each group's original order.
    return [...driverRows, ...driverlessTractorRows]
        .sort((a, b) => (a.hasChange === b.hasChange ? 0 : a.hasChange ? -1 : 1))
        .map(({ row }) => row);
}
